import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ReactFlow, {
  useNodesState,
  useEdgesState,
  Background,
  Controls,
  MiniMap,
  ReactFlowProvider,
  useReactFlow
} from 'reactflow';
import 'reactflow/dist/style.css';
import { useData } from '../../context/DataContext';
import { useLanguage } from '../../context/LanguageContext';
import { MousePointer2, GitBranch, ZoomIn, Move } from 'lucide-react';
import { calculateLayout, isQuestUnlocked, getWikiLink } from './layoutUtils';
import { QuestNode, LaneNode } from './KappaNodes';

function KappaPathContent({ profile, onUpdate }) {
  const memoizedNodeTypes = useMemo(() => ({ quest: QuestNode, lane: LaneNode }), []);
  const { quests: allQuests, isLoaded } = useData();
  const { t, language } = useLanguage();

  // MERKEZLEME FİXİ: fitView yerine setViewport kullanıyoruz
  const { setViewport } = useReactFlow();

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [completedQuests, setCompletedQuests] = useState(profile?.completedQuests || []);
  const [bounds, setBounds] = useState({ maxX: 0, maxY: 0 });
  const [initialLayoutDone, setInitialLayoutDone] = useState(false);

  useEffect(() => {
    setCompletedQuests(profile?.completedQuests || []);
  }, [profile?.completedQuests]);

  const kappaQuests = useMemo(() => {
    return isLoaded && allQuests ? allQuests.filter(q => q.kappaRequired) : [];
  }, [allQuests, isLoaded]);

  const handleToggleQuest = useCallback((questId) => {
    setCompletedQuests(prev => {
      const newCompleted = prev.includes(questId) ? prev.filter(id => id !== questId) : [...prev, questId];

      // KRİTİK FİX: React'ın hafızasındaki eski profili değil, o anki EN GÜNCEL profili LocalStorage'dan çekiyoruz!
      const rawStorage = localStorage.getItem('tarkovProfile');
      let currentProfile = rawStorage ? JSON.parse(rawStorage) : profile;

      if (currentProfile) {
        const updatedProfile = { ...currentProfile, completedQuests: newCompleted };
        localStorage.setItem('tarkovProfile', JSON.stringify(updatedProfile));

        // Render çakışmasını (Cannot update component App) önlemek için onUpdate'i event loop'un sonuna atıyoruz:
        setTimeout(() => {
          if (onUpdate) onUpdate(updatedProfile);
        }, 0);
      }
      return newCompleted;
    });
  }, [profile, onUpdate]);
  // KAMERA MERKEZLEME - NİHAİ ÇÖZÜM
  useEffect(() => {
    // Sadece node'lar hesaplanıp ekrana basılmaya hazır olduğunda kamerayı hareket ettir
    if (initialLayoutDone && nodes.length > 0) {
      setTimeout(() => {
        // x ve y: Haritayı Prapor'un sol üstüne çeker. zoom: Geniş açı sağlar.
        setViewport({ x: 100, y: 150, zoom: 0.045 }, { duration: 1000 });
      }, 100); // 100ms gecikme, React'ın DOM'u çizmesini bekler
    }
  }, [initialLayoutDone, nodes.length, setViewport]);
  useEffect(() => {
    if (!isLoaded || kappaQuests.length === 0 || initialLayoutDone) return;

    const layout = calculateLayout(kappaQuests, completedQuests, language, isQuestUnlocked, profile?.level || 1);

    const interactiveNodes = layout.nodes.map(n =>
      n.type === 'quest'
        ? { ...n, data: { ...n.data, onToggle: () => handleToggleQuest(n.id) } }
        : n
    );

    setNodes(interactiveNodes);
    setEdges(layout.edges);
    setBounds(layout.bounds);
    setInitialLayoutDone(true);



    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, kappaQuests, language, profile?.level, setViewport]);

  useEffect(() => {
    if (!initialLayoutDone) return;

    setNodes((nds) =>
      nds.map((node) => {
        if (node.type === 'quest') {
          const quest = kappaQuests.find(q => q.id === node.id);
          if (!quest) return node;

          const isCompleted = completedQuests.includes(quest.id);
          const isActive = !isCompleted && isQuestUnlocked(quest, completedQuests, profile?.level || 1);
          const isLocked = !isCompleted && !isActive;

          const label = quest.name;
          const traderDisplayName = quest.trader?.name || node.data.trader;
          const wikiLink = getWikiLink(quest, language);

          if (
            node.data.completed === isCompleted &&
            node.data.isActive === isActive &&
            node.data.locked === isLocked &&
            node.data.label === label &&
            node.data.trader === traderDisplayName &&
            node.data.wikiLink === wikiLink
          ) {
            return node;
          }

          return {
            ...node,
            data: {
              ...node.data,
              completed: isCompleted,
              isActive: isActive,
              locked: isLocked,
              label,
              trader: traderDisplayName,
              wikiLink
            }
          };
        } else if (node.type === 'lane') {
          const normalizedName = node.id.replace('lane-', '');
          const traderQuests = kappaQuests.filter(q => q.trader?.normalizedName === normalizedName);
          const traderDisplayName = traderQuests[0]?.trader?.name || normalizedName;

          if (node.data.trader === traderDisplayName) {
            return node;
          }

          return {
            ...node,
            data: {
              ...node.data,
              trader: traderDisplayName
            }
          };
        }
        return node;
      })
    );

    setEdges((eds) =>
      eds.map((edge) => {
        const isCompleted = completedQuests.includes(edge.target);
        if ((edge.style?.opacity === 0.7) === isCompleted) return edge;
        return {
          ...edge,
          style: {
            ...edge.style,
            stroke: isCompleted ? '#10b981' : '#3f3f46',
            opacity: isCompleted ? 0.7 : 0.3
          },
          markerEnd: {
            ...edge.markerEnd,
            color: isCompleted ? '#10b981' : '#3f3f46'
          }
        };
      })
    );
  }, [completedQuests, initialLayoutDone, kappaQuests, profile?.level, setNodes, setEdges, language, t]);

  if (!isLoaded) return <div className="h-screen bg-[#050505] flex items-center justify-center text-emerald-500 font-mono text-xl animate-pulse">{t('generating_map') || 'Initializing Neural Network...'}</div>;

  return (
    <div className="h-screen w-full bg-[#050505] relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 z-[100] bg-[#09090b]/80 backdrop-blur-md border-b border-white/5 h-16 px-6 flex items-center justify-between shadow-xl">
        <div className="flex items-center gap-4">
          <div className="w-8 h-8 bg-emerald-500/10 rounded-sm flex items-center justify-center border border-emerald-500/30">
            <GitBranch className="text-emerald-500 w-4 h-4" />
          </div>
          <h1 className="text-lg font-bold text-white tracking-widest uppercase">{t('kappa_path') || 'Kappa Path'}</h1>
          <span className="h-4 w-px bg-zinc-800 mx-2"></span>
          <p className="text-zinc-500 text-xs font-mono uppercase">{t('kappa_path_desc') || 'Interactive Tracker'}</p>
        </div>

        <div className="flex items-center gap-6 text-[10px] text-zinc-500 font-bold font-mono uppercase tracking-widest hidden md:flex">
          <div className="flex items-center gap-2"><MousePointer2 size={12} className="text-emerald-500" /> <span>{t('click_complete') || 'Click to Toggle'}</span></div>
          <div className="flex items-center gap-2"><Move size={12} className="text-emerald-500" /> <span>{t('drag_move') || 'Pan'}</span></div>
          <div className="flex items-center gap-2"><ZoomIn size={12} className="text-emerald-500" /> <span>{t('ctrl_zoom') || 'Zoom'}</span></div>
        </div>
      </div>

      <div style={{ width: '100%', height: 'calc(100vh - 64px)', marginTop: '64px' }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={memoizedNodeTypes}
          nodesDraggable={true}
          nodesConnectable={false}
          panOnScroll={true}
          zoomOnScroll={true}
          zoomActivationKeyCode="Control"
          panOnDrag={true}
          minZoom={0.03}
          maxZoom={0.6}
          translateExtent={[[-5000, -5000], [bounds.maxX + 5000, bounds.maxY + 5000]]}
          nodeExtent={[[-2000, -2000], [bounds.maxX + 2000, bounds.maxY + 2000]]}
          onlyRenderVisibleElements={true}
          defaultViewport={{ x: 1700, y: -500, zoom: 0.03 }}
        >
          <Background color="#18181b" gap={800} variant="dots" size={4} />

          <div style={{ position: 'absolute', bottom: '48px', right: '48px', zIndex: 1000, pointerEvents: 'auto' }} className="shadow-2xl">            <MiniMap
            nodeColor={n => n.type === 'lane' ? 'transparent' : (n.data.completed ? '#10b981' : n.data.isActive ? '#f59e0b' : '#27272a')}
            maskColor="rgba(5, 5, 5, 0.8)"
            style={{ backgroundColor: '#09090b', border: '1px solid #27272a', borderRadius: '4px', width: 250, height: 150 }}
            pannable
            zoomable
          />
          </div>

          <div style={{ position: 'absolute', bottom: '48px', right: '320px', zIndex: 1000, pointerEvents: 'auto' }} className="shadow-2xl">            <Controls
            showInteractive={false}
            style={{ backgroundColor: '#09090b', border: '1px solid #27272a', borderRadius: '4px', display: 'flex', flexDirection: 'row' }}
          />
          </div>
        </ReactFlow>
      </div>
    </div>
  );
}

function KappaPath(props) {
  return (
    <ReactFlowProvider>
      <KappaPathContent {...props} />

    </ReactFlowProvider>
  );
}

export default KappaPath;
